/**
 * Timeline entry for a standalone treatment (not linked to a consultation).
 * Supports edit and delete for uncommitted treatments via the kebab menu.
 */
import {
  CopyPlus,
  EllipsisVertical,
  FileSignature,
  Loader2,
  Pencil,
  Printer,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import type {
  PatientWithOwners,
  TreatmentPrescriptionContext,
  TreatmentWithRelations,
} from '@snootsvet/shared'
import {
  formatLatestWeight,
  formatName,
  getClinic,
  getPrimaryOrder,
  resolveDateFormat,
} from '@snootsvet/shared'

import { DrawerOverlay } from '@/frontend/components/drawer-overlay'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import { TreatmentForm } from '@/frontend/features/consultations/treatment-form'
import type { FormTreatmentItem } from '@/frontend/features/consultations/treatments.types'
import { toApiItem, toSavedItem } from '@/frontend/features/consultations/treatments.utils'
import { WrittenPrescriptionDialog } from '@/frontend/features/documents/written-prescription.form'
import {
  useRefillPrescription,
  useReprescribePrescription,
} from '@/frontend/features/patient/prescription-actions'
import { TreatmentRow } from '@/frontend/features/patient/timeline/treatment-row'
import {
  committedTreatmentToLabelData,
  shouldPrintLabel,
} from '@/frontend/features/printing/label-printing.utils'
import { usePrintLabels } from '@/frontend/features/printing/labels/use-print-labels'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useListPatientVitals } from '@/frontend/hooks/api/patients/use-list-patient-vitals'
import { useDeleteTreatment } from '@/frontend/hooks/api/use-delete-treatment'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useUpdateTreatment } from '@/frontend/hooks/api/use-update-treatment'

function GenerateScriptButton({ rx }: { rx: TreatmentWithRelations }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="ghost" size="icon-sm" onClick={() => setOpen(true)}>
        <FileSignature className="size-3.5" />
      </Button>
      {open && <WrittenPrescriptionDialog prescription={rx} open={open} onOpenChange={setOpen} />}
    </>
  )
}

export function TreatmentEntry({
  treatment,
  patient,
}: {
  treatment: TreatmentWithRelations & TreatmentPrescriptionContext
  /** The treatment's patient, supplied by the timeline — the edit form needs its species. */
  patient: PatientWithOwners
}) {
  const editable = !treatment.isCommitted
  // The dispensing order's clinic, shared with the menu so the printed label's
  // clinic matches the one its orders were billed at.
  const clinicId = getPrimaryOrder(treatment.orders)?.clinicId ?? null

  return (
    <div className="overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10">
      <TreatmentRow
        treatment={treatment}
        showMeta
        iconPosition="inline"
        menu={
          <div className="flex items-center gap-1">
            {treatment.isExternal && <GenerateScriptButton rx={treatment} />}
            <StandaloneTreatmentMenu
              treatment={treatment}
              patient={patient}
              clinicId={clinicId}
              editable={editable}
            />
          </div>
        }
      />
    </div>
  )
}

/** Kebab menu for standalone timeline treatment entries — edit, delete, re-prescribe, print. */
function StandaloneTreatmentMenu({
  treatment,
  patient,
  clinicId,
  editable,
}: {
  treatment: TreatmentWithRelations & TreatmentPrescriptionContext
  patient: PatientWithOwners
  /**
   * The dispensing order's clinic, resolved once by `TreatmentEntry` — sharing it
   * keeps the printed label's clinic identical to the one badged on the card.
   */
  clinicId: string | null
  editable: boolean
}) {
  const [editOpen, setEditOpen] = useState(false)
  const refill = useRefillPrescription({ rx: treatment, patientId: treatment.patientId })
  const represcribe = useReprescribePrescription({ rx: treatment, patientId: treatment.patientId })
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const { mutateAsync: updateTreatment } = useUpdateTreatment()
  const { mutateAsync: deleteTreatment } = useDeleteTreatment({ patientId: treatment.patientId })
  const { print, isPrinting } = usePrintLabels()
  const { region } = useViewerRegion()
  const { data: vitals } = useListPatientVitals(treatment.patientId)
  // Same rule the checkout screen prints by: only take-home dispensations get a label.
  const printable = treatment.orders.some(shouldPrintLabel)

  /** Generate and preview the dispensing label for this standalone treatment. */
  async function handlePrintLabel() {
    const filename = `label-${treatment.productInfo.name.toLowerCase().replace(/\s+/g, '-')}.pdf`
    await print(
      [
        committedTreatmentToLabelData({
          treatment,
          patient,
          ownerName: formatName(patient.owner),
          clinic: getClinic(clinicId),
          // No consultation vitals to draw on standalone, so the label carries the
          // patient's most recent recorded weight.
          weight: formatLatestWeight(vitals, region),
          // The label goes home with the owner, so its dates follow the
          // dispensing clinic and the owner's region, not the printing vet's.
          fmt: resolveDateFormat(clinicId, patient.owner.region),
        }),
      ],
      filename
    )
  }

  async function handleDelete() {
    setIsDeleting(true)
    try {
      await deleteTreatment(treatment.id)
    } catch {
      // Toast handled by hook
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" />}>
          {isDeleting ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <EllipsisVertical className="size-3.5" />
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          {editable && (
            <DropdownMenuItem onClick={() => setEditOpen(true)}>
              <Pencil className="size-3.5" />
              Edit
            </DropdownMenuItem>
          )}
          {editable && (
            <DropdownMenuItem
              className="text-destructive-fg"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              <Trash2 className="size-3.5" />
              Delete
            </DropdownMenuItem>
          )}
          {refill.menuItem}
          <DropdownMenuItem onClick={represcribe.trigger}>
            <CopyPlus className="size-3.5" />
            Re-prescribe
          </DropdownMenuItem>
          {printable && (
            <DropdownMenuItem disabled={isPrinting} onClick={handlePrintLabel}>
              <Printer className="size-3.5" />
              Print Label
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete treatment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the treatment and cancel any associated order.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DrawerOverlay>
            <DialogHeader>
              <DialogTitle>Edit Treatment — {treatment.productInfo.name}</DialogTitle>
            </DialogHeader>
            <EditTreatmentPanel
              treatment={treatment}
              patient={patient}
              onSave={async (item) => {
                try {
                  await updateTreatment({
                    id: treatment.id,
                    patientId: treatment.patientId,
                    body: toApiItem(item, region),
                  })
                  setEditOpen(false)
                } catch {
                  // Toast handled by hook
                }
              }}
              onCancel={() => setEditOpen(false)}
            />
          </DrawerOverlay>
        </DialogContent>
      </Dialog>

      {refill.dialog}
      {represcribe.dialog}
    </>
  )
}

/**
 * Inline edit form for a standalone treatment — renders the appropriate form
 * pre-populated with the treatment's current values.
 */
function EditTreatmentPanel({
  treatment,
  patient,
  onSave,
  onCancel,
}: {
  treatment: TreatmentWithRelations
  /**
   * A vaccine reaches this panel whenever its category resolves to 'vaccination', so the
   * form needs the species to narrow its disease list.
   */
  patient: PatientWithOwners
  onSave: (item: FormTreatmentItem) => Promise<void>
  onCancel: () => void
}) {
  const { data: staff } = useGetAuthenticatedStaff()
  const isRemoteVet = staff?.role === 'remote_vet'
  const [isSaving, setIsSaving] = useState(false)
  const defaults = useMemo(
    () =>
      toSavedItem({
        ...treatment,
        product: treatment.product ? { ...treatment.product, stock: [] } : null,
      }),
    [treatment]
  )
  const category = defaults.treatmentCategory

  async function handleSave(item: FormTreatmentItem) {
    setIsSaving(true)
    try {
      await onSave(item)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      {/* Category is fixed when editing — the selector is read-only. */}
      <TreatmentForm
        patientId={patient.id}
        category={category}
        defaults={defaults}
        prescribingContext="standalone"
        // No context clinic standalone — medications/consumables fall back to their
        // in-form clinic selector. Services are administered in consult and never
        // reach here: they always carry a consultationId, and the timeline only
        // renders consultation-less treatments standalone.
        contextClinicId={undefined}
        patientSpecies={patient.species}
        defaultCreateTask={isRemoteVet}
        onSave={handleSave}
        onCancel={onCancel}
        editing
        saving={isSaving}
      />
    </div>
  )
}
